import React from "react";
import { Range } from "react-range";
import { sizeNameMap } from "../utils/sizeNameUtils";

/**
 * Shared size filter component used by both main Filters and FishListing
 */
const SizeFilter = ({
  // Numeric size props
  includeNumericSizes,
  setIncludeNumericSizes,
  sizeRange,
  sizeFilter,
  onSizeFilterChange,
  // Non-numeric size props
  includeNonNumericSizes,
  setIncludeNonNumericSizes,
  availableNonNumericSizes = [],
  selectedNonNumericSizes = [],
  onNonNumericSizeChange,
  // Layout props
  compact = false,
  className = "",
}) => {
  const hasNumericSizes = sizeRange && sizeRange[0] !== sizeRange[1];
  const hasNonNumericSizes = availableNonNumericSizes.length > 0;
  const numericSizeMin = sizeRange?.[0] ?? 0;
  const numericSizeMax = sizeRange?.[1] ?? numericSizeMin;
  const clampNumericSize = (value) =>
    Math.min(numericSizeMax, Math.max(numericSizeMin, value));
  const clampedNumericSizes = [
    clampNumericSize(sizeFilter?.[0] ?? numericSizeMin),
    clampNumericSize(sizeFilter?.[1] ?? numericSizeMax),
  ].sort((left, right) => left - right);

  // Sort non-numeric sizes by standard order
  const sortedNonNumericSizes = [...new Set(availableNonNumericSizes)].sort(
    (a, b) => {
      const sizeOrder = Object.values(sizeNameMap);
      const indexA = sizeOrder.indexOf(a);
      const indexB = sizeOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    }
  );

  return (
    <>
      {/* Numeric Size Section */}
      {hasNumericSizes && (
        <div
          className={`size-filter-card bg-white rounded-lg p-3 shadow-sm border border-gray-200 flex-1 grow flex flex-col justify-center`}
        >
          <label className="flex items-center gap-2 cursor-pointer mb-2">
            <input
              type="checkbox"
              className="form-checkbox"
              checked={includeNumericSizes}
              onChange={(e) => setIncludeNumericSizes(e.target.checked)}
            />
            <span className="text-sm font-medium text-gray-700">
              Numeric Sizes
            </span>
          </label>

          <div className={`mt-3 px-2 ${!includeNumericSizes ? 'opacity-50 pointer-events-none' : ''}`}>
            <Range
              disabled={!includeNumericSizes}
              step={1}
              min={numericSizeMin}
              max={numericSizeMax}
              values={clampedNumericSizes}
              onChange={onSizeFilterChange}
              renderTrack={({ props, children }) => (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    height: "6px",
                    background: `linear-gradient(to right, #ccc ${
                      ((clampedNumericSizes[0] - numericSizeMin) /
                        (numericSizeMax - numericSizeMin)) *
                      100
                    }%, #3b82f6 ${
                      ((clampedNumericSizes[0] - numericSizeMin) /
                        (numericSizeMax - numericSizeMin)) *
                      100
                    }%, #3b82f6 ${
                      ((clampedNumericSizes[1] - numericSizeMin) /
                        (numericSizeMax - numericSizeMin)) *
                      100
                    }%, #ccc ${
                      ((clampedNumericSizes[1] - numericSizeMin) /
                        (numericSizeMax - numericSizeMin)) *
                      100
                    }%)`,
                    borderRadius: "3px",
                  }}
                >
                  {children}
                </div>
              )}
              renderThumb={({ props }) => (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    height: "16px",
                    width: "16px",
                    backgroundColor: "#3b82f6",
                    borderRadius: "50%",
                    outline: "none",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                  }}
                />
              )}
            />
            <div className="flex justify-between text-xs text-gray-600 mt-2">
              <span>{clampedNumericSizes[0]}"</span>
              <span>{clampedNumericSizes[1]}"</span>
            </div>
          </div>
        </div>
      )}

      {/* Non-Numeric Size Section */}
      {hasNonNumericSizes && (
        <div
          className={`size-filter-card bg-white rounded-lg p-3 shadow-sm border border-gray-200 flex-1 grow flex flex-col justify-center`}
        >
          <label className="flex items-center gap-2 cursor-pointer mb-2">
            <input
              type="checkbox"
              className="form-checkbox"
              checked={includeNonNumericSizes}
              onChange={(e) => setIncludeNonNumericSizes(e.target.checked)}
            />
            <span className="text-sm font-medium text-gray-700">
              Non-Numeric Sizes
            </span>
          </label>

          <div className={`mt-2 flex flex-col gap-1 max-h-[150px] overflow-y-auto ${!includeNonNumericSizes ? 'opacity-50' : ''}`}>
            {sortedNonNumericSizes.map((size) => (
              <label
                key={size}
                className={`flex items-center gap-2 p-1.5 rounded text-sm ${includeNonNumericSizes ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-not-allowed'}`}
              >
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={selectedNonNumericSizes.includes(size)}
                  onChange={() => onNonNumericSizeChange(size)}
                  disabled={!includeNonNumericSizes}
                />
                <span className="text-gray-700">{size}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default SizeFilter;
